/*
 * Client for the minimal my-api (services/my-api — allocation / invite /
 * claim, OnePulse slice 1). Base URL comes from VITE_MY_API_BASE per the
 * docs/MY-API-CONTRACT.md convention: unset ⇒ callers render an honest
 * unconfigured state; nothing is mocked.
 */

import { normalizeEmail } from '../auth/normalizeEmail'
import { createFifoLimiter } from './fetchLimiter'

export type HolderState = 'invited' | 'claimed' | 'ready' | 'consumed' | 'revoked'

export interface AllocationHolder {
  holderId: string
  state: HolderState
  allocatedEmail: string | null
  invitedAt: string
  inviteEmailSentAt: string | null
}

export interface OrderAllocations {
  orderId: string
  orderRef: string
  eventId: string
  eventName: string
  /** EVENT DATE CARRY (BOARDROOM-2026-09-01-175306 items 1–2; proposal
   *  MY-2026-09-01-180752): ISO `events.starts_at` / `events.ends_at` off the
   *  my-api orders read. `eventEndsAt` anchors the Current/Past orders
   *  partition (isPastEvent, src/data/pastEvents.ts); `eventStartsAt` is the
   *  REAL race-day anchor for assign-flow band checks (raceDayFrom). Optional/
   *  null on an older my-api or a degraded server read — the FE then keeps the
   *  order in Current (never wrongly buried) and anchors bands to now. */
  eventStartsAt?: string | null
  eventEndsAt?: string | null
  /** `ticketUnitId` (BOARDROOM-2026-08-17-210222): the team unit the ticket
   *  belongs to — groups a Team-of-N unit's N real ticket rows into per-seat
   *  assign targets. Optional so a pre-deploy my-api (no field) degrades to
   *  the typeName-grouping fallback rather than erroring.
   *
   *  `holderComplete` / `qrAvailable` (TOLERANT READS — Duncan's corrected
   *  deferred-child model, BOARDROOM-2026-09-02-152856): purchaser-visible
   *  signals for a seat DEFERRED to the child's own parent (guestparent).
   *  `holderComplete` = the holder (the parent) has completed profile +
   *  waiver; `qrAvailable` = the child's QR may be shown in the PURCHASER's
   *  account too (the buyer takes the kids to the venue). BOTH ABSENT on
   *  today's my-api — the server leg lands them in parallel; absent/null =
   *  today's behaviour (waiting states only, NO done flip, NO QR). The
   *  load-bearing rule: NO signature → NO QR anywhere — the FE never renders
   *  a QR affordance for a deferred seat from local/optimistic state; only
   *  `qrAvailable === true` unlocks it. */
  tickets: Array<{
    ticketId: string
    ticketUnitId?: string | null
    typeName: string
    holder: AllocationHolder | null
    holderComplete?: boolean | null
    qrAvailable?: boolean | null
  }>
}

export interface ClaimLookup {
  state: HolderState
  eventName: string
  eventStartsAt: string | null
  venue: string | null
  ticketTypeName: string
  invitedByName: string | null
  allocatedEmailMasked: string
  /** GUARDIAN CLAIM (S1/S2 wire contract, BOARDROOM-2026-09-03-052111 /
   *  CMT-2026-09-03-020; camelCase per the S1 build note — every /claim wire
   *  field is camelCase, snake_case being reserved for bus emits//svc/DB
   *  payloads): 'guardian' ⇔ this invite asks a PARENT/GUARDIAN to claim and
   *  manage a CHILD's seat — the child stays the participant; the parent
   *  signs FOR them. ABSENT (older my-api) or 'self' = today's self-claim
   *  behaviour, byte-identical rendering. */
  claimKind?: 'guardian' | 'self'
  /** The CHILD's name on guardian rows (never fabricated client-side);
   *  null/absent otherwise. */
  childName?: string | null
}

/** Normalised guardian-claim context off a claim lookup: non-null ⇔ the row is
 *  a guardian claim (claimKind === 'guardian'). `childName` is '' when the
 *  wire carried no usable name — callers render the honest name-less variant,
 *  never an invented child. */
export function guardianClaimContext(lookup: ClaimLookup): { childName: string } | null {
  if (lookup.claimKind !== 'guardian') return null
  return { childName: (lookup.childName ?? '').trim() }
}

export interface ClaimResult {
  holderId: string
  ticketId: string
  eventId: string
  eventName: string
  ticketTypeName: string
  /** ADDITIVE guardian carry on the POST /claim result too (S1 build note,
   *  camelCase) — the FE prefers the result's own facts for the post-claim
   *  record when present; absent (older my-api) falls back to the lookup's. */
  claimKind?: 'guardian' | 'self'
  childName?: string | null
}

/** Holder kind on the /me/holdings wire (child-holder slice, BOARDROOM
 *  launch-gate dispatch): who the seat is actually FOR. Optional end-to-end —
 *  an older my-api omits it, and every consumer must then behave exactly as
 *  before the field existed (self-subject rendering, no child labels). */
export type HolderKind = 'self' | 'child' | 'invitee' | 'anonymous'

export interface Holding {
  ticketId: string
  holderId: string
  eventId: string
  eventName: string
  ticketTypeName: string
  state: HolderState
  /** Per-holding subject kind (child-holder slice). ABSENT on an older
   *  my-api — tolerated everywhere; absent reads as today's behaviour. */
  kind?: HolderKind
  /** The child's full name when kind='child'; null/absent otherwise (older
   *  my-api or a non-child holder). NEVER fabricated client-side. */
  holderName?: string | null
}

/** Per-event heat policy as emitted by my-api's `event_heat_policy_v1` fold
 *  (CMT-2026-08-10-018). The view spells the allocated case `system-allocate`;
 *  the FE `HeatPolicy` type spells it `system-allocated` — `heatPolicyFromReadiness`
 *  in getReady.ts bridges the two. */
export type HeatPolicyValue = 'self-select' | 'system-allocate'

export interface Readiness {
  profileComplete: boolean
  waiverComplete: boolean
  heatComplete: boolean
  ready: boolean
  /** Per-event heat policy (CMT-2026-08-10-018). Optional/`null` when the server
   *  sends no signal — an older my-api build, or the settings view not yet
   *  applied — in which case the FE keeps its self-select default. */
  heatPolicy?: HeatPolicyValue | null
  /** `true` self-select · `false` system-allocate · absent/`null` no signal. */
  heatSel?: boolean | null
  /** ISO event start (nullable) — the chronological sort key for the get-ready
   *  home cards (CMT-121335). Optional: older my-api builds omit it, in which
   *  case the FE sort leaves those cards in stable order (degrades gracefully)
   *  until the my-api getReadiness change that emits it deploys. */
  eventStartsAt?: string | null
  /** The caller's CURRENTLY SELECTED heat start time as "HH:mm" (CMT-121325).
   *  Optional/`null` when no heat is chosen, or an older my-api that does not
   *  emit it — the wallet ticket then falls back to the box-office wave time. */
  selectedHeatTime?: string | null
  /** POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): SERVER-computed "the
   *  event has ended" (events.ends_at vs now, computed my-api-side at read
   *  time — the FE must NEVER re-derive clock logic). Drives the closed-state
   *  card (dim + "Event ended" badge + sort sink) and the gated assign/step
   *  CTAs. Optional: absent (older my-api) or false = event open. */
  eventClosed?: boolean
  /** ELIGIBILITY CARRY (BOARDROOM-2026-08-28-194055 item 4): the ticket's R1
   *  eligibility contract off the readiness read — the INVITEE's first in-hand
   *  contract (their Claim/Holding payloads never carried one; only the
   *  purchaser wire did). Feeds the ProfileWizard pre-save gate. `null` =
   *  honestly unconfigured/degraded server-side; absent = older my-api. Either
   *  way the FE gate skips — never a guessed restriction. */
  eligibility?: { gender: 'M' | 'F' | 'BOTH' | null; ageMin: number | null; ageMax: number | null } | null
  /** ISO race-day anchor for the age band (events.starts_at — the SAME anchor
   *  the server gate uses). Absent/null → the FE age axis is skipped. */
  raceDay?: string | null
  /** PAST-EVENT PARTITION carry (BOARDROOM-2026-09-01-175306 item 2): the RAW
   *  ISO `events.ends_at` instant off the readiness reads — the Tickets badge,
   *  wallet and Get-ready home exclude settled past events via the ONE shared
   *  boundary (isPastEvent + PAST_SETTLE_GRACE_MS, src/data/pastEvents.ts).
   *  Absent/null (older my-api, degraded read, kill-switch) ⇒ the event stays
   *  visible — never wrongly buried. */
  eventEndsAt?: string | null
}

export interface HeatOption {
  heatId: string
  label: string
  displayOrder: number
  // Canon heat-selection enrichment (D18) — optional so pre-extension
  // payloads/fixtures still typecheck; the UI renders only what is present
  // and never fabricates a time/capacity.
  /** Wall-clock start on the event day, "HH:MM" (canon bold "12:45"). */
  startTime?: string | null
  /** Canon "Wave {n}" ordinal. */
  waveNumber?: number | null
  /** Max participants (canon "{x} people" / "{left} OF {cap} LEFT"). */
  capacity?: number | null
  /** Live count of participants currently assigned to this heat. */
  assignedCount?: number | null
  /** capacity - assignedCount; null/absent when capacity is unknown. */
  spotsRemaining?: number | null
}

/** Caller-scoped header context returned with the type-scoped heats read
 *  (canon D15/D16 + free re-selection preselect). */
export interface HeatListContext {
  eventId: string
  eventName: string
  eventStartsAt: string | null
  ticketTypeId: string | null
  ticketTypeName: string | null
  /** The caller's ticket's current heat, if any — re-selection is FREE for a
   *  solo ticket. */
  assignedHeatId: string | null
  /** TEAM ticket = the caller's unit has >1 member; the team shares ONE heat. */
  isTeam?: boolean
  /** FIRST-PICKER-WINS lock (BOARDROOM-230739): true when this is a team whose
   *  heat is already set — the caller inherits it read-only and cannot move the
   *  team. Optional so a pre-deploy my-api (no field) degrades to the old free
   *  picker rather than erroring. */
  unitLocked?: boolean
}

export interface HeatCatalog {
  heats: HeatOption[]
  context: HeatListContext | null
}

export interface WaiverInput {
  signerName: string
  consented: boolean
  templateVersion: string
  templateHash: string
  signedAt: string
}

export type QrStatus = 'active' | 'consumed' | 'revoked'

/** Legacy account marry-up (CMT-035, Approach B = JIT link-on-login). The
 *  pre-fillable profile the my-api `/me/legacy-profile` endpoint returns on a
 *  legacy match. EVERY field is nullable — the datalake may hold only some, and
 *  a NULL is an honest "not present", never a fabricated placeholder. Shapes
 *  mirror the wizard's field homes (birthdate `YYYY-MM-DD`, phone E.164 where
 *  known) so `draftFromLegacy` is a straight transcription. */
export interface LegacyProfile {
  givenName: string | null
  familyName: string | null
  birthdate: string | null
  gender: string | null
  phone: string | null
  nationality: string | null
  address: string | null
  city: string | null
}

export interface LegacyProfileResult {
  linked: boolean
  profile: LegacyProfile | null
}

/** Identity-field verification level (LOCKED rule PROGRAMME-RULE-IDENTITY-
 *  FIELD-EDIT-LOCK-BY-VERIFICATION-LEVEL-V1; my scope per BOARDROOM-2026-08-
 *  29-114614): 3 = self-attested (default — free self-edit), 2 = event-ID-
 *  verified at check-in (identity fields not self-editable), 1 = third-party-
 *  verified (locked for everyone). Read-only on this surface — the promotion
 *  write (L3→L2) and the CS edit path live with other entities. */
export type IdentityVerificationLevel = 1 | 2 | 3

export function myApiBase(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_MY_API_BASE || null
}

function url(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}${path}`
}

async function check<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    let code = ''
    let reason: string | undefined
    let serverMessage: string | undefined
    try {
      const body = (await res.json()) as { error?: string; reason?: string; message?: string }
      code = body.error ?? ''
      // Eligibility 422s (BOARDROOM-2026-08-28-194055) carry a machine `reason`
      // (gender|age) + the server's plain-English `message` — captured so the
      // FE can surface the exact reject per ticket and map copy per locale.
      reason = typeof body.reason === 'string' ? body.reason : undefined
      serverMessage = typeof body.message === 'string' ? body.message : undefined
    } catch {
      /* body not json */
    }
    throw new MyApiError(`${what} failed (${res.status})`, res.status, code, reason, serverMessage)
  }
  return (await res.json()) as T
}

export class MyApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    /** Machine axis on an eligibility 422 (gender|age) — absent otherwise. */
    public reason?: string,
    /** The server's plain-English reject copy — the honest fallback when no
     *  locale mapping applies. NEVER fabricated client-side. */
    public serverMessage?: string,
  ) {
    super(message)
  }
}

/** Max concurrent GET /me/orders/{id}/allocations requests
 *  (BOARDROOM-2026-09-01-225419 item 2). 6 because (a) browsers historically
 *  cap ~6 HTTP/1.1 connections per origin, so a larger fan-out buys nothing on
 *  the wire, and (b) 6 stays comfortably inside API-gateway/Lambda burst
 *  limits — the 241-card burst is what rate-limited/500'd and degraded every
 *  card at once. */
export const ALLOCATIONS_FETCH_LIMIT = 6

/** ONE limiter shared by every fetchOrderAllocations caller (each OrderCard's
 *  useOrderSeats, refresh(), single-card views) — that sharing is the point:
 *  the bound is per-app, not per-card. */
const allocationsLimiter = createFifoLimiter(ALLOCATIONS_FETCH_LIMIT)

/** Bounded to ALLOCATIONS_FETCH_LIMIT concurrent requests via the shared FIFO
 *  limiter above — with N order cards mounted, at most 6 GETs are in flight
 *  and the rest queue in mount order. The limited task spans the request AND
 *  the body parse (the slot models the whole connection) and captures only
 *  this call's four small arguments (WU6 scope lesson). The slot is released
 *  on failure too (check() throws inside the task), and the useOrderSeats
 *  retry/backoff (PR#103) calls this function afresh per attempt — every
 *  retry RE-ACQUIRES a slot, never bypasses the limiter. */
export async function fetchOrderAllocations(
  idToken: string,
  base: string,
  orderId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OrderAllocations> {
  return allocationsLimiter(async () => {
    const res = await fetchImpl(url(base, `/me/orders/${orderId}/allocations`), {
      headers: { Authorization: `Bearer ${idToken}` },
    })
    return check(res, 'allocations fetch')
  })
}

/** Guestparent parent-permission ask (Duncan order BOARDROOM-2026-09-02-150714
 *  / comms dispatch BOARDROOM-150801): when the allocation is a request for a
 *  PARENT to sign for a child, pass their names so the server emits the
 *  dedicated parent_sign_request letter greeting the PARENT (never the
 *  allocator, never the child). Omit for a plain allocation — the wire body
 *  is then byte-identical to today. */
export interface ParentSignInput {
  parentName: string
  childName: string
}

export async function allocateTicket(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  email: string,
  fetchImpl: typeof fetch = fetch,
  // ADDITIVE, deliberately AFTER fetchImpl: existing call sites (positional
  // fetchImpl included) compile and behave unchanged.
  parentSign?: ParentSignInput,
): Promise<{ holderId: string; state: HolderState; allocatedEmail: string; inviteEmailSent: boolean }> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/allocation`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    // Invitee email is normalised at the boundary — it becomes the invited
    // Cognito identity downstream (BOARDROOM-2026-08-24-183930 #50 FIX A).
    // parent_sign rides only when supplied (spread-omit) — absent keeps the
    // body byte-identical to the pre-parent-sign client.
    body: JSON.stringify({
      email: normalizeEmail(email),
      ...(parentSign ? { parent_sign: parentSign } : {}),
    }),
  })
  return check(res, 'allocation')
}

/** Child-holder wire input (launch-gate slice; contract fixed with the my-api
 *  leg). `dob` is 'YYYY-MM-DD'; the contract requires it, but the FE types it
 *  nullable so an (unreachable-today: childDob is Required) DOB-less record
 *  rides as an honest null and the SERVER's 422 is the reject surface — the
 *  client never fabricates a date. `gender` is the wire vocabulary 'M'|'F',
 *  null for unknown / self-described (the server's gender gate conservative-
 *  allows null, mirroring the FE's own genderEligible posture). */
export interface ChildHolderInput {
  child: {
    firstName: string
    lastName: string
    dob: string | null
    gender: 'M' | 'F' | null
  }
  /** false = the purchaser's OWN child; true = someone else's child signed
   *  for under a stated permission (TWO POOLS, PRODUCT-RULES §Children). */
  guest: boolean
}

/** Create a CHILD holder on a purchased seat (launch-gate slice: the child /
 *  guestchild / teen-MINE confirms become server-backed). Same error surface
 *  as allocateTicket: eligibility 422s carry {reason:'age'|'gender'|
 *  'duplicate_participant', message}; a live holder is 409/422
 *  already_allocated; a closed event is 409 event_ended. 201 returns the new
 *  holderId plus the seat's lean readiness. */
export async function createChildHolder(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  input: ChildHolderInput,
  fetchImpl: typeof fetch = fetch,
): Promise<{ holderId: string; readiness: Readiness }> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/child-holder`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check(res, 'child-holder')
}

export async function revokeAllocation(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/allocation/revoke`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  await check(res, 'revoke')
}

// ---------------------------------------------------------------------------
// Holdings + get-ready (WU-2/WU-3, dispatch BOARDROOM-2026-08-07-233226)
// ---------------------------------------------------------------------------

/** Actor TYPE on a removed-holdings entry (build item #6, BOARDROOM-2026-09-
 *  05-121056): WHO caused the change — the ONLY actor fact the FE ever
 *  renders. Recipient identity is NEVER on this type (privacy default:
 *  "transferred to another participant", actor type only). */
export type RemovedHoldingActorType = 'owner' | 'support' | 'system'

/** One holding this account USED TO have (transferred away / reassigned /
 *  CS-changed) — the ADDITIVE optional `removedHoldings[]` carry on the
 *  /me/holdings wire (server leg lands in parallel; absent = today's my-api =
 *  today's behaviour). Exactly the five display facts and NOTHING else — the
 *  parse seam below strips any other field, so recipient identity can never
 *  even reach the render layer. */
export interface RemovedHolding {
  eventName: string
  ticketTypeName: string | null
  /** What happened as the wire words it ('transferred' | 'reassigned' |
   *  'claimed-elsewhere' | 'revoked' | …). Owner-line display copy is
   *  change-verb-aware (review fix F2a): only a transfer-shaped verb claims a
   *  transfer; 'revoked'/unknown reads "removed by the order owner". */
  change: string | null
  /** ISO instant of the change; null/unparseable ⇒ rendered without a date
   *  (honest absence, never a fabricated one). Rendered UTC-anchored
   *  (shortChangeDate, review fix F3) to match the server audit log. */
  occurredAt: string | null
  /** Out-of-domain/absent reads null → the actor-SILENT "{head} — changed on
   *  {date}" phrasing (review fix F2b) — never a guessed actor, and never
   *  "updated automatically" (that asserts a system actor). */
  actorType: RemovedHoldingActorType | null
}

const nonEmptyString = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null

/** TOLERANT parse of the raw wire field. The contract is RECONCILED with the
 *  my-api leg (review fix F6 — the earlier speculative spelling aliases
 *  removedAt|at|when / action|what are gone): the field names are exactly
 *  eventName / ticketTypeName / change / occurredAt / actorType, and the
 *  server feed is recency-bounded (90 days, newest 10) and excludes
 *  live-held tickets — wire shape unchanged. Tolerance is for MALFORMED data
 *  only, which degrades to absence: non-array → [], junk/event-less entries
 *  dropped, unknown actorType → null. Exported for the contract tests. */
export function normalizeRemovedHoldings(raw: unknown): RemovedHolding[] {
  if (!Array.isArray(raw)) return []
  const out: RemovedHolding[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const r = entry as Record<string, unknown>
    const eventName = nonEmptyString(r.eventName)
    if (!eventName) continue // a headless line ("— — transferred…") helps no one
    const actor = r.actorType
    out.push({
      eventName,
      ticketTypeName: nonEmptyString(r.ticketTypeName),
      change: nonEmptyString(r.change),
      occurredAt: nonEmptyString(r.occurredAt),
      actorType: actor === 'owner' || actor === 'support' || actor === 'system' ? actor : null,
    })
  }
  return out
}

export interface HoldingsRead {
  holdings: Holding[]
  /** [] on an older my-api (field absent) — every consumer then behaves
   *  exactly as before the field existed. */
  removedHoldings: RemovedHolding[]
}

/** Full /me/holdings read: live holdings + the removed-holdings carry. */
export async function fetchHoldingsRead(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<HoldingsRead> {
  const res = await fetchImpl(url(base, '/me/holdings'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  const body = await check<{ holdings: Holding[]; removedHoldings?: unknown }>(res, 'holdings fetch')
  return { holdings: body.holdings, removedHoldings: normalizeRemovedHoldings(body.removedHoldings) }
}

export async function fetchMyHoldings(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Holding[]> {
  return (await fetchHoldingsRead(idToken, base, fetchImpl)).holdings
}

/** Legacy account marry-up (CMT-035): fetch the caller's legacy profile for
 *  pre-fill. The endpoint keys on the caller's VERIFIED token email (never a
 *  body param — no PII in the request), links `users.legacy_object_id` on a
 *  match (append-only/idempotent, server-side), and returns { linked, profile }.
 *  No match / pending datalake lookup ⇒ { linked:false, profile:null }; the
 *  endpoint degrades to that on any failure, so this never blocks login. */
export async function fetchLegacyProfile(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LegacyProfileResult> {
  const res = await fetchImpl(url(base, '/me/legacy-profile'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'legacy profile fetch')
}

/** Fetch the caller's identity-field verification level from the my-api
 *  user-scoped read (`GET /me/identity-verification` — new small route per the
 *  dispatch: the existing me-scoped reads are holding-scoped (/me/holdings,
 *  /me/readiness are per-ticket and EMPTY for a zero-holding user) or
 *  side-effectful (/me/legacy-profile links on call), while the level is
 *  USER-scoped and must be readable with zero holdings).
 *
 *  TOLERANT PARSE: only an explicit 1 or 2 locks — anything else (absent
 *  field, out-of-domain value) reads 3, the rule's default. Fetch/HTTP
 *  failures THROW (MyApiError) so the caller decides; the profile surface
 *  fails OPEN to 3 (see profileStore.loadIdentityVerificationLevel) — never
 *  lock a user out of their own profile on a read failure. */
export async function fetchIdentityVerificationLevel(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<IdentityVerificationLevel> {
  const res = await fetchImpl(url(base, '/me/identity-verification'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  const { level } = await check<{ level: unknown }>(res, 'identity-verification fetch')
  return level === 1 || level === 2 ? level : 3
}

/** Get-or-create my own kind='self' holder for a ticket I purchased. */
export async function ensureSelfHolder(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ holderId: string }> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/holder`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'holder bootstrap')
}

export async function fetchReadiness(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/readiness`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'readiness fetch')
}

/** Batched readiness for ALL of the caller's live holdings in ONE request
 *  (CMT-150431) — the get-ready home prefers this over the per-ticket N+1 that
 *  hung large orders (~18 tickets). A 404 means an older my-api WITHOUT the
 *  `/me/readiness` route; the caller catches it (MyApiError.status === 404) and
 *  falls back to the per-holding `fetchEventSteps` fan-out, so the FE degrades
 *  gracefully until the my-api change deploys. */
export async function fetchAllReadiness(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Array<{ ticketId: string; readiness: Readiness }>> {
  const res = await fetchImpl(url(base, '/me/readiness'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return (
    await check<{ readiness: Array<{ ticketId: string; readiness: Readiness }> }>(res, 'batched readiness fetch')
  ).readiness
}

/** Records profile completion on the server for a held ticket (flips
 *  `ticket_holders.profile_complete`). Called by getReady.ts `onProfileSaved`
 *  when the wizard saves a COMPLETE profile.
 *
 *  NOTE (Ruling C tension, BOARDROOM-2026-08-09-133534): Ruling C retired this
 *  call, intending the server to DERIVE profile readiness. That derivation was
 *  never implemented — my-api holds no participant-profile data (it lives in
 *  Cognito), so this endpoint is the ONLY thing that can set the flag, and
 *  nothing calling it left the get-ready profile step permanently "missing"
 *  (the CMT-043 live-test bug). Re-wired as a launch necessity; a Ruling C
 *  amendment should confirm this seam (or replace it with a server-side
 *  participant-profile store that derives completion). See onProfileSaved. */
export async function markProfileComplete(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/readiness/profile-complete`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'profile-complete')
}

export async function submitWaiver(
  idToken: string,
  base: string,
  ticketId: string,
  input: WaiverInput,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/waiver`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check(res, 'waiver submit')
}

export async function fetchEventHeats(
  base: string,
  eventId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<HeatOption[]> {
  const res = await fetchImpl(url(base, `/events/${eventId}/heats`))
  return (await check<{ heats: HeatOption[] }>(res, 'heats fetch')).heats
}

/** Canon heat-selection read (D15–D18). With { idToken, ticketId } the server
 *  scopes heats to the caller's ticket type (FULL ones included) and returns
 *  header context (event date, type name, current assignment). Without it,
 *  falls back to the legacy public unscoped list (context: null). */
export async function fetchHeatCatalog(
  base: string,
  eventId: string,
  opts?: { idToken: string; ticketId: string },
  fetchImpl: typeof fetch = fetch,
): Promise<HeatCatalog> {
  if (!opts) {
    const res = await fetchImpl(url(base, `/events/${eventId}/heats`))
    const { heats } = await check<{ heats: HeatOption[] }>(res, 'heats fetch')
    return { heats, context: null }
  }
  const res = await fetchImpl(
    url(base, `/events/${eventId}/heats?ticketId=${encodeURIComponent(opts.ticketId)}`),
    { headers: { Authorization: `Bearer ${opts.idToken}` } },
  )
  const body = await check<{ heats: HeatOption[]; context?: HeatListContext | null }>(res, 'heats fetch')
  return { heats: body.heats, context: body.context ?? null }
}

export async function assignHeat(
  idToken: string,
  base: string,
  ticketId: string,
  heatId: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/heat`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ heatId }),
  })
  return check(res, 'heat assign')
}

/**
 * Bare-token QR (BOARDROOM-2026-08-24-192147 GO; design note
 * MY-2026-08-24-180947): ONE authed GET returns the caller's own ticket's
 * stable scan token (`tickets.anonymous_scan_token`) — THE QR payload,
 * rendered directly. Replaces the retired mintQr/fetchActiveQr pair (the
 * server keeps `qr/mint` alive only for the previously-deployed FE). Server
 * guard = live holder + the same readiness gate as the old mint (409
 * not_ready before get-ready completes); `token:null` = the token is not
 * available on this environment (honest absence, never fabricated).
 */
export async function fetchScanToken(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ token: string | null }> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/scan-token`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'scan token fetch')
}

/** PURCHASER-scoped scan-token twin (my-api PR#125; Duncan item-5 amendment
 *  BOARDROOM-2026-09-02-152856): the buyer wallet's QR payload for a seat
 *  DEFERRED to the child's own parent. The holder-scoped fetchScanToken above
 *  is myLiveHolder-gated and 404s for the buyer — this order-addressed route
 *  serves the SAME {token} shape behind the server's signing-complete gate
 *  (the same gate that drives the allocations `qrAvailable` flag: the token
 *  serves ⇔ qrAvailable===true). Used ONLY for deferred-admitted wallet
 *  stubs; the buyer's own/held seats keep the holder path. */
export async function fetchPurchaserScanToken(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ token: string | null }> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/scan-token`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'purchaser scan token fetch')
}

// ---------------------------------------------------------------------------
// Event questionnaire — my-api BFF over settings' /svc seam
// (SETTINGS-2026-08-25-105204; agreed contract MY-2026-08-25-054534: the BFF
// proxies server-side with the shared service token — the FE NEVER holds it).
// Shapes are the settings pass-through (snake_case on the wire, unreshaped).
// ---------------------------------------------------------------------------

/** One selectable option — CONTRACT (amends the PR#310 note; ratified both
 *  sides: MY-2026-09-01-194520 pin + SETTINGS-2026-09-01-200559 confirm):
 *  `label_i18n` (Record locale→string) is the CANONICAL label shape, resolved
 *  CLIENT-side at render time (locale lives in the client; a server-flat
 *  label would bake one locale into the wire). Flat `label` is LEGACY
 *  optional — wins when non-empty (settings' v1 fitness rows {label,value}
 *  still carry it). Renderer resolution: label → label_i18n[locale] →
 *  label_i18n.en → first value → String(value). */
export interface QuestionnaireOption {
  value: string | number
  label?: string | null
  label_i18n?: Record<string, string> | null
}

export interface QuestionnaireQuestion {
  question_id: string
  prompt: string
  qtype: string
  /** Option set for choice questions; null/absent → free-text fallback. */
  options?: QuestionnaireOption[] | null
  /** Settings PR#310: `true` → render the choice question as a type-ahead
   *  dropdown; absent/false → button/checkbox choices. There is NO list-length
   *  heuristic. Tolerated-absent so older payloads (no field) keep rendering
   *  as buttons — EXCEPT that the LIVE resolved wire (BOARDROOM-2026-09-01-
   *  183926 probe) still self-describes dropdowns via legacy qtype:"dropdown"
   *  with no searchable field at all, so the renderer also honours that
   *  declaration (see isSearchable in QuestionnaireFlow). */
  searchable?: boolean
  required: boolean
}

/** BFF read result. The get-ready step renders IFF active && show &&
 *  !submitted; submitted → done; !active or !show → step absent entirely. */
export interface QuestionnaireStatus {
  active: boolean
  show: boolean
  submitted: boolean
  questions: QuestionnaireQuestion[]
}

export async function fetchQuestionnaire(
  idToken: string,
  base: string,
  eventId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<QuestionnaireStatus> {
  const res = await fetchImpl(url(base, `/me/events/${encodeURIComponent(eventId)}/questionnaire`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'questionnaire fetch')
}

/** Records ONE answer via the BFF (POST …/questionnaire/answer). The server
 *  keys it on the caller's verified Cognito sub — the FE sends no identity.
 *  answer_value is the V62 OBJECT shape `{value: …}` — settings'
 *  questionnaireSvc rejects a bare string/number with a 400 (Duncan-hit live,
 *  BOARDROOM-2026-08-28-164014: every fitness submit failed). The caller
 *  passes the option's resolved score (fitness: 3|2|1) or the free-text
 *  string; the wrapping lives HERE so no call site can regress to bare.
 *
 *  MULTI-SELECT (answer-shape v1, CONFIRMED by SETTINGS-2026-08-31-183042): a
 *  multiple_choice answer passes an ARRAY of the selected option values,
 *  serialised as `answer_value: { value: [v1, v2, …] }` inside the same V62
 *  envelope the single-select path uses — single-select/free-text are
 *  byte-for-byte unchanged when no labels are supplied. Choice answers SHOULD
 *  also carry display labels (multi `labels:[…]`, single `label:"…"`) so the
 *  admin participants CSV prints display strings; absent labels degrade fine
 *  settings-side. */
export async function submitQuestionnaireAnswer(
  idToken: string,
  base: string,
  eventId: string,
  questionId: string,
  answerValue: string | number | Array<string | number>,
  // Display labels for choice answers (answer-shape v1 SHOULD, SETTINGS-2026-
  // 08-31-183042): multi → labels:[…], single → label:"…" — the admin CSV then
  // prints display strings. Absent (free text / older callers) degrades fine.
  labels?: string[],
  fetchImpl: typeof fetch = fetch,
): Promise<{ answer_id: string; stored: boolean }> {
  const answer_value = Array.isArray(answerValue)
    ? { value: answerValue, ...(labels && labels.length > 0 ? { labels } : {}) }
    : { value: answerValue, ...(labels && labels.length === 1 ? { label: labels[0] } : {}) }
  const res = await fetchImpl(url(base, `/me/events/${encodeURIComponent(eventId)}/questionnaire/answer`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ question_id: questionId, answer_value }),
  })
  return check(res, 'questionnaire answer')
}

export async function lookupClaim(
  base: string,
  inviteToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ClaimLookup> {
  const res = await fetchImpl(url(base, `/claim/${inviteToken}`))
  return check(res, 'claim lookup')
}

export async function claimInvite(
  idToken: string,
  base: string,
  inviteToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ClaimResult> {
  const res = await fetchImpl(url(base, `/claim/${inviteToken}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'claim')
}

/**
 * /claim smart-path (BOARDROOM-222954): provision the INVITED account (public,
 * keyed by the invite token — the invited email is resolved server-side, never
 * sent from the client). 'created' ⇒ a new account was made with `password`
 * (sign in with it); 'exists' ⇒ the account already exists (the entered password
 * is tried as the existing one). `email` is the full invited address to sign in.
 */
export async function createClaimAccount(
  base: string,
  inviteToken: string,
  password: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ outcome: 'created' | 'exists'; email: string }> {
  const res = await fetchImpl(url(base, `/claim/${inviteToken}/create-account`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  return check(res, 'create-account')
}

// ---------------------------------------------------------------------------
// Pending-claim continuity across the hosted-UI OAuth round trip: the claim
// token is stashed before redirecting to Cognito and consumed when the app
// boots with a session. localStorage (not sessionStorage) because signup can
// bounce through an email-verification step in a fresh tab.
// ---------------------------------------------------------------------------

const PENDING_CLAIM_KEY = 'my.pendingClaimToken'

export function storePendingClaim(token: string): void {
  localStorage.setItem(PENDING_CLAIM_KEY, token)
}

export function takePendingClaim(): string | null {
  const t = localStorage.getItem(PENDING_CLAIM_KEY)
  if (t) localStorage.removeItem(PENDING_CLAIM_KEY)
  return t
}

export function peekPendingClaim(): string | null {
  return localStorage.getItem(PENDING_CLAIM_KEY)
}

// ---------------------------------------------------------------------------
// Legacy marry-up pre-fill marker (CMT-035). The email-first login finishes
// with a full-page redirect, so the "open the profile wizard pre-filled from
// the legacy account" signal must survive the reload. We carry ONLY a boolean
// marker — NEVER the profile — so no PII lands in localStorage (nor any URL /
// query string). The wizard re-fetches the (idempotent) legacy profile via the
// authed endpoint when the marker is present, then clears it.
// ---------------------------------------------------------------------------

const LEGACY_PREFILL_KEY = 'my.legacyPrefillPending'

export function markLegacyPrefillPending(): void {
  localStorage.setItem(LEGACY_PREFILL_KEY, '1')
}

/** Read-and-clear the marker (one-shot). */
export function takeLegacyPrefillPending(): boolean {
  const v = localStorage.getItem(LEGACY_PREFILL_KEY)
  if (v) localStorage.removeItem(LEGACY_PREFILL_KEY)
  return v === '1'
}
